import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { getInitData } from '@/lib/tg';
import { TopBar } from '@/components/ui/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader2, Upload, X, Edit3, Check } from 'lucide-react';
import { BalanceWidget } from '@/components/BalanceWidget';

interface Character {
  id: string;
  url: string;
}
interface ContentJob {
  id: string;
  format: string;
  status: string;
  resultUrls: string[] | null;
  createdAt: string;
}

interface ScriptSlide {
  index: number;
  beat: string;
  on_image_text: string;
  accent_word?: string | null;
  image_prompt: string;
}
interface ScriptPanel {
  index: number;
  beat: string;
  narration: string;
  on_image_text: string;
  image_prompt: string;
}
interface PreviewResult {
  script: {
    character_appearance?: string;
    palette_tokens?: string[];
    camera_style_note?: string;
    panels?: ScriptPanel[];
    slides?: ScriptSlide[];
    hero_image_prompt?: string;
    motion_prompt?: string;
    voiceover?: string;
    on_image_text?: string;
    product_summary?: string;
  } & Record<string, unknown>;
  extract: unknown;
  caption: {
    caption: string;
    hook_125: string;
    hashtags: string[];
    warning: string | null;
  } | null;
  format: 'comic' | 'carousel' | 'story' | 'reels';
}

const MAX_PHOTOS = 4;

async function uploadPhotos(files: File[]): Promise<string[]> {
  const fd = new FormData();
  for (const f of files) fd.append('photos', f);
  const res = await fetch('/api/studios/upload', {
    method: 'POST',
    headers: { 'X-Telegram-Init-Data': getInitData() },
    body: fd,
  });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error?.error ?? 'upload_failed');
  return json.data.urls as string[];
}

export default function ContentStudioPage() {
  const qc = useQueryClient();
  const [format, setFormat] = useState<'comic' | 'carousel' | 'story' | 'reels'>('comic');
  const [characterId, setCharacterId] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const photoUrls = useMemo(() => photos.map((f) => URL.createObjectURL(f)), [photos]);
  useEffect(() => () => photoUrls.forEach(URL.revokeObjectURL), [photoUrls]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);

  const { data: chars } = useQuery<Character[]>({
    queryKey: ['characters'],
    queryFn: () => api.get<Character[]>('/api/studios/characters'),
  });
  const { data: jobs } = useQuery<ContentJob[]>({
    queryKey: ['content-jobs'],
    queryFn: () => api.get<ContentJob[]>('/api/studios/content'),
    refetchInterval: 15_000, // сами обновляем пока генерится
  });

  // 1) Preview: генерит только script+caption, не тратит FAL на картинки
  const previewMut = useMutation({
    mutationFn: async () => {
      setUploading(true);
      try {
        const urls = photos.length > 0 ? await uploadPhotos(photos) : [];
        setUploadedUrls(urls);
        return await api.post<PreviewResult>('/api/studios/content/preview', {
          format,
          inputText: text,
          referencePhotos: urls,
        });
      } finally {
        setUploading(false);
      }
    },
    onSuccess: (data) => setPreview(data),
  });

  // 2) Confirm: запускает генерацию картинок с (возможно отредактированным) preview
  const submitMut = useMutation({
    mutationFn: async (opts: { usePreview: boolean }) => {
      setUploading(true);
      try {
        const urls =
          opts.usePreview && uploadedUrls.length > 0
            ? uploadedUrls
            : photos.length > 0
              ? await uploadPhotos(photos)
              : [];
        return await api.post<{ id: string; status: string }>('/api/studios/content', {
          format,
          characterId: characterId ?? undefined,
          inputText: text,
          referencePhotos: urls,
          ...(opts.usePreview && preview
            ? {
                presetScript: preview.script,
                presetExtract: preview.extract as Record<string, unknown>,
                presetCaption: preview.caption,
              }
            : {}),
        });
      } finally {
        setUploading(false);
      }
    },
    onSuccess: () => {
      setText('');
      setPhotos([]);
      setPreview(null);
      setUploadedUrls([]);
      qc.invalidateQueries({ queryKey: ['content-jobs'] });
    },
  });

  function onPickPhotos(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []).slice(0, MAX_PHOTOS - photos.length);
    setPhotos((prev) => [...prev, ...files].slice(0, MAX_PHOTOS));
    e.target.value = ''; // чтоб можно было выбрать те же файлы заново
  }

  function removePhoto(i: number) {
    setPhotos((prev) => prev.filter((_, idx) => idx !== i));
  }

  const canSubmit = photos.length > 0 || text.trim().length >= 5;

  // Оценка стоимости (совпадает с серверной формулой в content-worker.ts):
  //   BASE_LLM $0.025 (vision strict+inference + script) + CAPTION $0.005
  //   per slide: image ($0.05 nano-banana / $0.035 flux) + XML-prompter ($0.003)
  //   Reels: +$0.60 seedance i2v
  const slidesForFormat: Record<string, number> = { comic: 4, carousel: 5, story: photos.length > 1 ? photos.length : 1 };
  const slideCount = slidesForFormat[format] ?? 1;
  const perSlideImage = photos.length > 0 ? 0.05 : 0.035;
  const perSlide = perSlideImage + 0.003;
  const baseLlm = 0.025 + 0.005;
  const estimatedUsd =
    format === 'reels' ? baseLlm + perSlide + 0.6 : baseLlm + perSlide * slideCount;

  return (
    <div className="safe-bottom min-h-full">
      <TopBar title="Контент-студия" />
      <div className="p-4 space-y-3">
        <BalanceWidget estimatedUsd={estimatedUsd} />

        <Card className="space-y-3">
          <div className="font-semibold">Формат</div>
          <div className="grid grid-cols-4 gap-2">
            {(['comic', 'carousel', 'story', 'reels'] as const).map((f) => (
              <Button
                key={f}
                variant={format === f ? 'primary' : 'secondary'}
                onClick={() => {
                  setFormat(f);
                  // Сбрасываем preview — script от предыдущего формата невалиден
                  // для нового (разные схемы: panels у comic, slides у остальных).
                  if (preview && preview.format !== f) {
                    setPreview(null);
                  }
                }}
              >
                {f === 'comic'
                  ? 'Комикс 4'
                  : f === 'carousel'
                    ? 'Карусель 5'
                    : f === 'story'
                      ? 'Сторис 1'
                      : '🎬 Reel'}
              </Button>
            ))}
          </div>
          {format === 'reels' && (
            <p className="text-xs text-tg-hint">
              Reel — 5-секундное видео через seedance image-to-video. ~30–60 сек генерация.
            </p>
          )}
          {format === 'story' && photos.length > 1 && (
            <p className="text-xs text-tg-hint">
              📸 Серия из {photos.length} сторис — каждая с одним товаром из загруженных фото.
            </p>
          )}
        </Card>

        {/* multi-photo upload */}
        <Card className="space-y-3">
          <div className="flex justify-between items-center">
            <div className="font-semibold">Референсы ({photos.length}/{MAX_PHOTOS})</div>
            <span className="text-xs text-tg-hint">image2image · до 4 фото</span>
          </div>

          <div className="grid grid-cols-4 gap-2">
            {photos.map((f, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden">
                <img
                  src={photoUrls[i]}
                  alt={`ref-${i}`}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 bg-black/60 rounded-full p-1"
                  aria-label="Убрать"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <label className="aspect-square rounded-xl border-2 border-dashed border-tg-hint/40 flex flex-col items-center justify-center text-tg-hint cursor-pointer hover:bg-tg-secondaryBg/40">
                <Upload className="w-5 h-5" />
                <span className="text-[10px] mt-1">Добавить</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onPickPhotos}
                />
              </label>
            )}
          </div>
          {photos.length === 0 && (
            <p className="text-xs text-tg-hint">
              Без референсов → чистый text2image. С референсами → image2image (банка / персонаж / сцена из ваших фото).
            </p>
          )}
        </Card>

        {chars && chars.length > 0 && (
          <Card className="space-y-3">
            <div className="font-semibold">Персонаж (опционально)</div>
            <div className="grid grid-cols-3 gap-2">
              {chars.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCharacterId(characterId === c.id ? null : c.id)}
                  className={
                    'rounded-xl overflow-hidden border-2 ' +
                    (characterId === c.id ? 'border-brand-orange' : 'border-transparent')
                  }
                >
                  <img src={c.url} alt={c.id} className="w-full h-28 object-cover" />
                  <div className="text-xs py-1">{c.id}</div>
                </button>
              ))}
            </div>
          </Card>
        )}

        <Card>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-full min-h-[100px] p-3 bg-tg-bg rounded-xl outline-none resize-none"
            placeholder={
              format === 'comic'
                ? 'Сюжет комикса: что рассказать клиенту, какой продукт продвигаем…'
                : format === 'carousel'
                  ? 'Тема карусели: главный оффер, преимущества, call-to-action…'
                  : 'Сторис: короткая мысль / эмоция, 10–15 слов.'
            }
          />
        </Card>

        {/* Кнопки: Превью (script-only, дёшево) + Запустить (полная генерация) */}
        {!preview && (
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="secondary"
              size="lg"
              disabled={!canSubmit || previewMut.isPending || submitMut.isPending || uploading}
              onClick={() => previewMut.mutate()}
            >
              {(previewMut.isPending || uploading) && (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              )}
              Превью сценария
            </Button>
            <Button
              variant="primary"
              size="lg"
              disabled={!canSubmit || previewMut.isPending || submitMut.isPending || uploading}
              onClick={() => submitMut.mutate({ usePreview: false })}
            >
              {(submitMut.isPending || uploading) && (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              )}
              Сразу сгенерировать
            </Button>
          </div>
        )}

        {preview && (
          <PreviewEditor
            preview={preview}
            onChange={setPreview}
            onConfirm={() => submitMut.mutate({ usePreview: true })}
            onCancel={() => setPreview(null)}
            confirmDisabled={submitMut.isPending || uploading}
            confirmLoading={submitMut.isPending || uploading}
          />
        )}

        {jobs && jobs.length > 0 && (
          <Card className="space-y-2">
            <div className="font-semibold">История</div>
            {jobs.slice(0, 10).map((j) => (
              <div
                key={j.id}
                className="flex items-center justify-between gap-2 text-sm py-1 border-b border-tg-secondaryBg/60 last:border-0"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="font-mono text-xs text-tg-hint">#{j.id}</span>
                  <span>{j.format}</span>
                  <span
                    className={
                      j.status === 'done'
                        ? 'text-green-500 text-xs'
                        : j.status === 'failed'
                          ? 'text-red-500 text-xs'
                          : 'text-tg-hint text-xs'
                    }
                  >
                    {j.status === 'running' ? '🎨 генерация…' : j.status}
                  </span>
                </div>
                {j.resultUrls && j.resultUrls.length > 0 && (
                  <a
                    href={j.resultUrls[0]}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-brand-orange"
                  >
                    Открыть ({j.resultUrls.length})
                  </a>
                )}
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}

/**
 * PreviewEditor — редактор сценария и caption'а перед запуском генерации картинок.
 * Пользователь может подкрутить on_image_text слайдов (что будет на картинке) и
 * финальный caption. При «Запустить» — script+caption идут в POST /content как
 * presetScript/presetCaption, worker скипает повторную script-LLM-стадию.
 */
function PreviewEditor({
  preview,
  onChange,
  onConfirm,
  onCancel,
  confirmDisabled,
  confirmLoading,
}: {
  preview: PreviewResult;
  onChange: (p: PreviewResult) => void;
  onConfirm: () => void;
  onCancel: () => void;
  confirmDisabled: boolean;
  confirmLoading: boolean;
}) {
  const { script, caption, format } = preview;
  const panels: ScriptPanel[] = script.panels ?? [];
  const slides: ScriptSlide[] = script.slides ?? [];

  function updateSlideText(i: number, newText: string) {
    const next: PreviewResult = { ...preview, script: { ...script } };
    if (script.panels) {
      next.script = {
        ...script,
        panels: panels.map((p, idx) => (idx === i ? { ...p, on_image_text: newText } : p)),
      };
    } else if (script.slides) {
      next.script = {
        ...script,
        slides: slides.map((s, idx) => (idx === i ? { ...s, on_image_text: newText } : s)),
      };
    }
    onChange(next);
  }

  function updateCaption(newCaption: string) {
    if (!caption) return;
    onChange({
      ...preview,
      caption: { ...caption, caption: newCaption, hook_125: newCaption.slice(0, 125) },
    });
  }

  const items = format === 'comic' ? panels : slides;

  return (
    <Card className="space-y-3 border-2 border-brand-orange/40">
      <div className="flex items-center gap-2">
        <Edit3 className="w-4 h-4 text-brand-orange" />
        <div className="font-semibold">Превью сценария</div>
        <span className="ml-auto text-xs text-tg-hint">
          редактируй и запускай
        </span>
      </div>

      {caption?.warning && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-xs text-red-500">
          ⚠️ {caption.warning}
        </div>
      )}

      {script.character_appearance && (
        <details className="text-xs">
          <summary className="text-tg-hint cursor-pointer">Персонаж (character_appearance)</summary>
          <div className="mt-1 p-2 bg-tg-bg rounded-lg text-tg-text">
            {script.character_appearance}
          </div>
        </details>
      )}

      {script.product_summary && (
        <div className="text-xs text-tg-hint">
          Продукт: <span className="text-tg-text">{script.product_summary}</span>
        </div>
      )}

      <div className="space-y-2">
        <div className="text-xs font-semibold text-tg-hint uppercase">
          {format === 'comic' ? 'Панели комикса' : format === 'reels' ? 'Hero-кадр' : 'Слайды'}
        </div>

        {format === 'reels' && (
          <div className="space-y-2">
            <div className="text-xs text-tg-hint">Hero image prompt:</div>
            <textarea
              value={script.hero_image_prompt ?? ''}
              onChange={(e) =>
                onChange({ ...preview, script: { ...script, hero_image_prompt: e.target.value } })
              }
              className="w-full min-h-[80px] p-2 text-xs bg-tg-bg rounded-lg outline-none resize-y"
            />
            <div className="text-xs text-tg-hint">Voiceover:</div>
            <textarea
              value={script.voiceover ?? ''}
              onChange={(e) =>
                onChange({ ...preview, script: { ...script, voiceover: e.target.value } })
              }
              className="w-full min-h-[60px] p-2 text-xs bg-tg-bg rounded-lg outline-none resize-y"
            />
            <div className="text-xs text-tg-hint">On-image text (3-5 слов):</div>
            <input
              value={script.on_image_text ?? ''}
              onChange={(e) =>
                onChange({ ...preview, script: { ...script, on_image_text: e.target.value } })
              }
              className="w-full p-2 text-xs bg-tg-bg rounded-lg outline-none"
            />
          </div>
        )}

        {format !== 'reels' &&
          items.map((item, i) => (
            <div key={i} className="p-2 bg-tg-bg rounded-lg space-y-1">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-mono text-tg-hint">#{i + 1}</span>
                <span className="text-brand-orange font-semibold">{item.beat}</span>
              </div>
              <textarea
                value={item.on_image_text}
                onChange={(e) => updateSlideText(i, e.target.value)}
                className="w-full min-h-[50px] p-2 text-xs bg-tg-secondaryBg rounded-lg outline-none resize-y"
                placeholder="Текст на картинке"
              />
            </div>
          ))}
      </div>

      {caption && (
        <div className="space-y-1">
          <div className="text-xs font-semibold text-tg-hint uppercase">Caption для Instagram</div>
          <textarea
            value={caption.caption}
            onChange={(e) => updateCaption(e.target.value)}
            className="w-full min-h-[180px] p-3 text-xs bg-tg-bg rounded-lg outline-none resize-y"
          />
          {caption.hashtags.length > 0 && (
            <div className="text-xs text-tg-hint">
              Хэштеги: {caption.hashtags.join(' ')}
            </div>
          )}
          <div className="text-xs text-tg-hint">
            Первые 125 симв (hook до «…ещё»): «{caption.hook_125}»
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 pt-2">
        <Button variant="secondary" onClick={onCancel}>
          Отменить
        </Button>
        <Button
          variant="primary"
          onClick={onConfirm}
          disabled={confirmDisabled}
        >
          {confirmLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          <Check className="w-4 h-4 mr-1" />
          Запустить
        </Button>
      </div>
    </Card>
  );
}
